import { ImgAndVideoUrls } from './Screen.types';

export enum OrientationType {
  Landscape = 'Landscape',
  Portrait = 'Portrait',
}

export enum PlaylistItemType {
  API,
  ITEM,
}

export enum PlaylistType {
  Image = 'Image',
  Video = 'Video',
  Template = 'Template',
}

export interface PlaylistItem {
  id?: number;
  imageUrl?: string;
  name: string;
  duration: string;
  type?: string;
  playlistType?: PlaylistType;
  playlistId?: number;
  campaignId?: number;
  sortOrder: number;
  template?: any;
  itemType?: PlaylistItemType;
}

export interface InitialValues {
  name: string;
  items: PlaylistItem[];
  orientationType: OrientationType;
}

export interface FindOneResponse {
  id: number;
  name: string;
  items: PlaylistItem[];
  orientationType?: OrientationType;
  updatedAt?: string;
}

export interface CreatePlaylistProps {
  readonly handleCreatePlaylist: (values: InitialValues) => void;
  readonly isSubmitting: boolean;
}

export interface PlaylistData {
  name: string;
  items: PlaylistItem[];
  updatedAt?: string;
}

export interface PlaylistFormProps {
  id?: number;
  playlistData?: PlaylistData;
  readonly submitHandler: (values: InitialValues) => void;
  readonly isSubmitting: boolean;
}

export interface Playlist {
  id: number;
  name: string;
  liveNow: string[];
  duration: string;
  imgAndVideoUrls: ImgAndVideoUrls[];
  status: 'active' | 'inactive';
  orientation: OrientationType;
}

export interface PlaylistsResponse {
  count: number;
  playlists: Playlist[];
}

export interface PlaylistUIProps {
  handleDeletePlaylist: () => void;
  playlistData?: Playlist[];
  isLoading?: boolean;
  isDeleting: boolean;
  open: boolean;
}

export interface ItemModifier {
  name: string;
  price: number;
}

export interface Schedules {
  id: number;
  day: string;
  startTime: string;
  endTime: string;
  availabilityType: string;
  status: string;
}

export interface CategoryItemsProps {
  id: number;
  itemName: string;
  itemDescription: string;
  itemPrice: string;
  itemPicture?: string;
  modifiers?: Partial<Record<string, number>>;
  outOfStock?: boolean;
  schedules?: Schedules[] | null;
}

export interface MenuCategoriesProps {
  id: number;
  name: string;
  index?: number; // Right now just for template 3
  items?: CategoryItemsProps[];
  schedules?: Schedules[] | null;
}

export interface SelectedMenus {
  id: number;
  name: string;
  categories: MenuCategoriesProps[];
  schedules?: Schedules[] | null;
}

interface CustomStyleProps {
  fontSize: number;
  fontStyle: string;
  fontWeight: string;
  color: string;
  textTransform: string;
  marginBottom: object | string;
}

interface BaseProperties {
  key: string;
  isEnabled: boolean;
  color: string;
}

interface Heading extends BaseProperties {
  value: string;
  style: Partial<CustomStyleProps>;
  // style: SxProps<Theme> & Partial<CustomStyleProps>;
}

interface Background {
  color: string;
  image: string;
  label: string;
  isEnabled: boolean;
}

export enum TextScaleSize {
  Half = 0.5,
  Base = 1,
  OneAndHalf = 1.5,
}

interface CatgoryProperties {
  color: string;
  underlineColor: string;
  backgroundColor?: string;
  isEnabled: boolean;
  textScale: TextScaleSize;
  fontWeight: number;
  fontStyle: string;
  letterSpacing: number;
  lineHeight: number;
}

export enum TemplateMediaType {
  Image = 'Image',
  Video = 'Video',
}

export interface TemplateMediaContent {
  id: number;
  name: string;
  url: string;
  type: TemplateMediaType;
  contentType: string;
}

interface TemplateMedia {
  mediaContent: TemplateMediaContent | null;
  isEnabled: boolean;
}

interface BrandLogo {
  label: string;
  isEnabled: boolean;
  image?: string | null;
}

export interface Template {
  templateId: number;
  fontFamily?: string;
  fontFamilyName?: string;
  categoryLimit?: number;
  itemsLimit: number;
  maxItemsLimit: number;
  minItemsLimit: number;
  heading?: Heading;
  subHeading?: Heading;
  brandLogo?: BrandLogo;
  category?: Partial<CatgoryProperties>;
  item: {
    textScale?: number;
    itemName: {
      color: string;
      fontWeight: number;
      fontStyle: string;
      letterSpacing: number;
      lineHeight: number;
    };
    itemPrice: {
      color: string;
      isEnabled: boolean;
      fontWeight: number;
      fontStyle: string;
      letterSpacing: number;
      lineHeight: number;
      backgroundColor: string;
    };
    itemDescription?: {
      isEnabled: boolean;
      color?: string;
      fontWeight: number;
      fontStyle: string;
      letterSpacing: number;
      lineHeight: number;
    };
    itemImage?: {
      isEnabled: boolean;
    };
  };
  itemImage?: {
    label: string;
    isEnabled: boolean;
  };
  itemDescription?: {
    label: string;
    isEnabled: boolean;
    color?: string;
  };
  itemPrice?: {
    label: string;
    isEnabled: boolean;
  };
  taxLabel: { isEnabled: boolean; taxText: string };
  layoutType?: number | null;
  media?: Partial<TemplateMedia>;
  background: Background;
  content?: SelectedMenus[] | [];
  orientation: 'Portrait' | 'Landscape';
  oddRowsBackgroundColor?: string;
  evenRowsBackgroundColor?: string;
  itemImages: {
    isEnabled: boolean;
    overflowImages: boolean;
  };
}

export interface TemplateMenus {
  menuId: number;
  playlistItemId: number;
}

export interface TemplateCategories {
  menuId: number;
  categoryId: number;
  playlistItemId: number;
}

export interface TemplateItems {
  categoryId: number;
  itemId: number;
  playlistItemId: number;
}

export interface TemplateContent {
  id: number;
  name: string;
  categories: MenuCategoriesProps[];
}

export interface StellarPlaylist {
  id: string;
  name: string;
  sortOrder: number;
  items: {
    id: number;
    name: string;
    duration: string;
    imageUrl: string;
    sortOrder: 1;
    type: string;
    playlistType: PlaylistType;
    template: Template;
  }[];
  schedules: Schedules[] | null;
}

export interface PlaylistTemplate {
  name: string;
  id: number | null;
  open: boolean;
}

export interface SortedItemsProps extends CategoryItemsProps {
  hasValidModifiers: boolean;
}

export interface TemplateProps {
  template: Template;
  scale?: number;
  development?: boolean;
  style?: { width: string; height?: string; aspectRatio?: string };
  timezone?: string;
  tenantId?: number;
  templateRef?: React.RefObject<HTMLDivElement>;
  active?: boolean;
  orientation?: ScreenOrientation;
  shouldLoad?: boolean;
}

export type FormattingKey = 'category' | 'itemName' | 'itemPrice' | 'itemDescription';

// Properties that can be updated
export type FormattingUpdate = Partial<{
  color: string;
  fontWeight: number;
  fontStyle: string;
  letterSpacing: number;
  lineHeight: number;
  underlineColor: string; // Only for category
  isEnabled: true;
}>;
